import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { sendPasswordResetEmail, signOut } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ProfilePreferences } from "@/components/profile/ProfilePreferences";
import { ArrowLeft, Loader2, KeyRound, LogOut, UserCircle, Bell, Shield, SlidersHorizontal } from "lucide-react";

const TABS = ["account", "notifications", "preferences", "security"] as const;
type Tab = typeof TABS[number];

export default function Settings() {
  const { user, loading: authLoading } = useAuth() as any;
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = (language as "en" | "fr") || "en";

  const [prefs, setPrefs] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const initialHash = (location.hash || "").replace("#", "");
  const initialTab: Tab = (TABS as readonly string[]).includes(initialHash) ? (initialHash as Tab) : "account";
  const [tab, setTab] = useState<Tab>(initialTab);

  useEffect(() => {
    const h = (location.hash || "").replace("#", "");
    if ((TABS as readonly string[]).includes(h) && h !== tab) setTab(h as Tab);
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = lang === "fr"
    ? {
        title: "Paramètres", subtitle: "Gérez votre compte, vos notifications et votre sécurité.",
        back: "Retour", account: "Compte", notifications: "Notifications", preferences: "Préférences", security: "Sécurité",
        email: "Courriel", name: "Nom", editProfile: "Modifier le profil complet",
        passwordReset: "Réinitialiser le mot de passe", passwordResetDesc: "Recevez un courriel pour changer votre mot de passe.",
        sendReset: "Envoyer le courriel", resetSent: "Courriel de réinitialisation envoyé",
        signOut: "Se déconnecter", signOutDesc: "Terminer votre session sur cet appareil.",
        signOutBtn: "Déconnexion", saved: "Préférences enregistrées",
      }
    : {
        title: "Settings", subtitle: "Manage your account, notifications and security.",
        back: "Back", account: "Account", notifications: "Notifications", preferences: "Preferences", security: "Security",
        email: "Email", name: "Name", editProfile: "Edit full profile",
        passwordReset: "Reset password", passwordResetDesc: "Receive an email to change your password.",
        sendReset: "Send email", resetSent: "Password reset email sent",
        signOut: "Sign out", signOutDesc: "End your session on this device.",
        signOutBtn: "Sign out", saved: "Preferences saved",
      };

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.id));
        const d = snap.exists() ? snap.data() : {};
        setPrefs((d as any).preferences || {});
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  const savePreferences = useCallback(async (next: any) => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await setDoc(doc(db, "users", user.id), { preferences: next, updatedAt: serverTimestamp() }, { merge: true });
      setPrefs(next);
    } finally {
      setSaving(false);
    }
  }, [user?.id]);

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    setResetting(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      toast({ title: t.resetSent });
    } catch (e: any) {
      toast({ title: e?.message || "Error", variant: "destructive" });
    } finally {
      setResetting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (e: any) {
      toast({ title: e?.message || "Error", variant: "destructive" });
    }
  };

  const onTabChange = (v: string) => {
    setTab(v as Tab);
    navigate({ pathname: "/settings", hash: `#${v}` }, { replace: true });
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t.back}
      </Button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t.title}</h1>
        <p className="text-muted-foreground text-sm">{t.subtitle}</p>
      </div>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="account"><UserCircle className="h-4 w-4 mr-2" />{t.account}</TabsTrigger>
          <TabsTrigger value="notifications"><Bell className="h-4 w-4 mr-2" />{t.notifications}</TabsTrigger>
          <TabsTrigger value="preferences"><SlidersHorizontal className="h-4 w-4 mr-2" />{t.preferences}</TabsTrigger>
          <TabsTrigger value="security"><Shield className="h-4 w-4 mr-2" />{t.security}</TabsTrigger>
        </TabsList>

        <TabsContent value="account" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t.account}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t.email}</Label>
                <Input value={user?.email || ""} disabled />
              </div>
              <div>
                <Label>{t.name}</Label>
                <Input value={user?.displayName || ""} disabled />
              </div>
              <Button asChild variant="outline">
  <Link to="/booking-settings">
    {language === 'fr' ? 'Gérer mes disponibilités de réservation' : 'Manage my booking availability'}
  </Link>
</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <ProfilePreferences initial={prefs || undefined} onSave={savePreferences} saving={saving} />
        </TabsContent>

        <TabsContent value="preferences" className="mt-6">
          <ProfilePreferences initial={prefs || undefined} onSave={savePreferences} saving={saving} />
        </TabsContent>

        <TabsContent value="security" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" />{t.passwordReset}</CardTitle>
              <CardDescription>{t.passwordResetDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handlePasswordReset} disabled={resetting || !user?.email}>
                {resetting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t.sendReset}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><LogOut className="h-5 w-5" />{t.signOut}</CardTitle>
              <CardDescription>{t.signOutDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                {t.signOutBtn}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
